import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
} from "@angular/core";
import { NgControl, ValidationErrors } from "@angular/forms";
import { forkJoin, Subject, Subscription } from "rxjs";
import { errorMessages, pattern, required } from "../constants/input-focus.constants";
import { HttpService } from "@app/@core/services/http.service";
import { take, takeUntil } from "rxjs";

/*
 * Usage:
 * <input formControl='[[controlName]]' inputFocus />
 *
 * Reset the control   error on focus by calling touch event.
 *
 */

const errorMsgs = Object.freeze({
  required: required,
  pattern : pattern,
  
});

const validationMsgData: ValidationMsgData = {
  errorMsgs: errorMsgs,
  icon: false,
};

@Directive({
  selector: "[inputFocus]",
  standalone: true
})
export class InputFocusDirective implements OnDestroy {
  validationData: any;
  errorMessages:any;
  statusChangeSubscription: Subscription;
  @Input("inputFocus") set data(val: ValidationMsgData) {
    if (val) {
      this.validationData = {
        ...validationMsgData,
        ...val,
        errorMsgs: {
          ...validationMsgData.errorMsgs,
          ...val.errorMsgs,
        },
      };
    } else {
      this.validationData = validationMsgData;
    }
  }
  @Input() loadErrorMessages: boolean = false;
  errorId: string;
    private destroy$ = new Subject<void>();
  constructor(
    private el: ElementRef,
    private formControl: NgControl,
    private renderer: Renderer2,
    private httpService: HttpService
  ) { }

  private loadMessages(): void {
    if (this.loadErrorMessages && !this.errorMessages) {
      forkJoin([
        this.httpService.get('/domain/lookup?requestType=MESSAGES'),
      ]).pipe(take(1), takeUntil(this.destroy$)).subscribe({
        next: (item: any) => {
          this.errorMessages = item[0].data;
        },
        error: (err) => {
          console.error('Failed to load error messages:', err);
        }
      });
    }
  }

  ngOnInit(): void {
    this.el.nativeElement.setAttribute('autocomplete', 'off');
    this.loadMessages();
    this.statusChangeSubscription = this.formControl.statusChanges.subscribe(
      (status) => {
        if (status === "VALID") {
          this.removeError();
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.statusChangeSubscription) {
      this.statusChangeSubscription.unsubscribe();
    }
  }

  @HostListener('blur')
  handleBlurEvent(): void {
    if (this.formControl.control.value && this.formControl.control.value.trim() == '') {
      this.formControl.control.reset();
    }
    if(this.formControl.name.toString().includes('SSN')){
      if(this.formControl.control.value ==='000000000' || this.formControl.control.value ==='999999999'){
        this.formControl.control.setErrors({ pattern: true });
      }
    }
    if (this.formControl.errors) {
      this.displayError();
    } else {
      this.removeError();
    }
  }


  @HostListener("focusin")
  onFocusIn() {
    this.formControl.control.markAsUntouched({ onlySelf: true });
    const id: string = this.el.nativeElement.id;
    if (id.includes("_date_input")) {
      this.errorId = "err_" + id;
    }
    this.removeError();
  }

  private removeError(): void {
    const errorElement = document.getElementById(this.errorId);
    if (errorElement) {
      errorElement.remove();
    }
    this.renderer.removeClass(this.el.nativeElement, "has-err-border");
  }

  private displayError() {
    this.removeError();
    this.errorId = "err_" + this.formControl.name;
    this.createDivElement(this.errorId);
  }

  private createDivElement(elId: string): void {
    const div: HTMLElement = this.renderer.createElement("div");
    const errMsg = this.createMsg();
    let message: any;
    if (errMsg != undefined) {
      message = this.renderer.createText(errMsg);
      if (message instanceof Node) {
        this.renderer.appendChild(div, message);
      }
    }
    div.setAttribute("aria-describedby", errMsg);
    div.setAttribute("id", elId);
    div.classList.add("err-font", "text-danger");
    this.renderer.addClass(this.el.nativeElement, "has-err-border");
    const parent: HTMLElement = this.el.nativeElement.parentElement;
    if (parent.classList.contains("input-group")) {
      this.renderer.appendChild(parent.parentElement, div);
    } else {
      this.renderer.appendChild(parent, div);
    }
  }

  private createMsg(): string {
    const valErrors: ValidationErrors = this.formControl.errors;
    const firstKey = Object.keys(valErrors)[0];
    let errorMsgList = this.validationData.errorMsgs[firstKey];
    let errorCode = errorMsgList.find(item => item.key === this.formControl.name);
    if(errorCode && this.errorMessages){
        const errorMessageObj = this.errorMessages.find(item => item.key === errorCode.value);
        if (errorMessageObj) {
            return errorMessageObj.value;
        }else{
          return errorMessages[errorCode.value];
        }

    }
    // if (firstKey === "minlength" && this.errorId === "err_strRoutingNbr") {
    //   errorMsg = this.validationData.errorMsgs["strRoutingNbr"];
    // } else if (firstKey === "morePayAmount" && this.errorId === "err_bdOtherPaymentAmount") {
    //   errorMsg = this.validationData.errorMsgs["morePayAmount"];
    // }
  }
  
}

export interface ValidationMsgData {
  errorMsgs?: any;
  icon?: boolean;
}